import { useApi } from './api';
import type { Client, PaginatedResponse, ClientCreateDTO, ReferralSummary, ApiResponse } from '../types';

export const useClientService = () => {
  const { apiFetch } = useApi();

  return {
    getClientsPaginated: async (page: number, size: number, search?: string, origin?: string): Promise<PaginatedResponse<Client>> => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      // ✅ NUEVO: Agregar parámetro origin (siempre se envía, incluso si es 'ALL')
      if (origin) {
        params.append('origin', origin);
      }

      try {
        const response = await apiFetch(`/clients?${params}`);

        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers.get('content-type'));

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response body:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text();
          console.error('Non-JSON response:', responseText);
          throw new Error('El servidor no devolvió JSON válido');
        }

        const data = await response.json();
        console.log('Clients data received:', data);
        return data;

      } catch (error) {
        console.error('Error in getClientsPaginated:', error);
        throw error;
      }
    },

    getClientById: async (id: number): Promise<Client> => {
      const response = await apiFetch(`/clients/${id}`);

      if (!response.ok) {
        throw new Error(`Error fetching client: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    },

    getClientReferrals: async (id: number): Promise<ReferralSummary[]> => {
      const response = await apiFetch(`/clients/${id}/referrals`);

      if (!response.ok) {
        throw new Error(`Error fetching client referrals: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    },

    createClient: async (client: ClientCreateDTO): Promise<ApiResponse<Client>> => {
      const response = await apiFetch('/clients', {
        method: 'POST',
        body: JSON.stringify(client),
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al crear el cliente',
        };
      }

      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    updateClient: async (id: number, client: ClientCreateDTO): Promise<ApiResponse<Client>> => {
      const response = await apiFetch(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(client),
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al actualizar el cliente',
        };
      }

      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    deleteClient: async (id: number): Promise<ApiResponse<void>> => {
      const response = await apiFetch(`/clients/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          error: data.error || 'Error al eliminar el cliente',
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Cliente eliminado exitosamente',
      };
    },

    getTotalClients: async (): Promise<number> => {
      const response = await apiFetch('/clients/count');

      if (!response.ok) {
        throw new Error('Error fetching clients count');
      }

      return await response.json();
    },

    getClientsWithReferrals: async (): Promise<number> => {
      const response = await apiFetch('/clients/with-referrals/count');

      if (!response.ok) {
        throw new Error('Error fetching clients with referrals count');
      }

      return await response.json();
    },

    getClientsWithoutReferrals: async (): Promise<number> => {
      const response = await apiFetch('/clients/without-referrals/count');

      if (!response.ok) {
        throw new Error('Error fetching clients without referrals count');
      }

      return await response.json();
    },

    getDashboardStats: async (): Promise<Record<string, number>> => {
      const response = await apiFetch('/clients/dashboard/stats');

      if (!response.ok) {
        throw new Error('Error fetching client dashboard stats');
      }

      return await response.json();
    },
    getClientDashboardStats: async (): Promise<{
      total: number;
      sinReferrals: number;
      terminados: number;
      nuevosEsteMes: number;
    }> => {
      const response = await apiFetch('/clients/dashboard/stats');

      if (!response.ok) {
        throw new Error('Error fetching client dashboard stats');
      }

      return await response.json();
    }

  };
};